import {
  useCreate,
  useDataProvider,
  useDelete,
  useGetIdentity,
  useNavigation,
  useNotification,
  useUpdate,
} from "@refinedev/core";

import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { Sentry } from "@/lib/sentry";
import type { IIdentity } from "@/providers/auth-provider";
import type {
  RunTriggerResponse,
  SiteGroupCreatePayload,
  SiteGroupUpdatePayload,
  TinaSiteGroup,
} from "@/types/tina";

export const useGroupsRepository = () => {
  const { custom, getOne } = useDataProvider()("tina");
  const { mutateAsync: createAsync } = useCreate();
  const { mutateAsync: updateAsync } = useUpdate();
  const { mutateAsync: deleteAsync } = useDelete();
  const { open } = useNotification();
  const { show } = useNavigation();
  const { data: identity } = useGetIdentity<IIdentity>();

  async function list(): Promise<TinaSiteGroup[]> {
    const response = await custom!({
      url: "",
      method: "get",
      meta: { urlPath: "site-groups" },
    });
    return response.data as TinaSiteGroup[];
  }

  async function get(id: string): Promise<TinaSiteGroup> {
    const response = await getOne!({ id, resource: "tina-groups" });
    return response as unknown as TinaSiteGroup;
  }

  async function create(
    payload: SiteGroupCreatePayload,
  ): Promise<TinaSiteGroup> {
    const response = await createAsync({
      resource: "tina-groups",
      values: payload,
      successNotification: { type: "success", message: `Created ${payload.name}` },
      errorNotification: false,
    });
    return response.data as unknown as TinaSiteGroup;
  }

  async function update(
    id: string,
    payload: SiteGroupUpdatePayload,
  ): Promise<TinaSiteGroup> {
    const response = await updateAsync({
      resource: "tina-groups",
      id,
      values: payload,
      successNotification: { type: "success", message: "Group updated" },
      errorNotification: false,
    });
    return response.data as unknown as TinaSiteGroup;
  }

  async function remove(id: string): Promise<void> {
    await deleteAsync({
      resource: "tina-groups",
      id,
      successNotification: { type: "success", message: "Group deleted" },
      errorNotification: false,
    });
  }

  async function runNow(id: string): Promise<RunTriggerResponse> {
    Sentry.addBreadcrumb({
      category: "tina-groups.run-now",
      message: "Running group now",
      level: "info",
      data: { id },
    });
    try {
      const response = await custom!({
        url: "",
        method: "post",
        payload: { triggered_by: identity?.id },
        meta: { urlPath: `site-groups/${encodeURIComponent(id)}/run-now` },
      });
      const result = response.data as RunTriggerResponse;
      open?.({
        type: "success",
        message: "Run triggered",
        description: `${result.seeded.length} sites`,
      });
      show("tina-runs", result.run_id);
      return result;
    } catch (error) {
      Sentry.captureException(error);
      open?.({
        type: "error",
        message: "Failed to run group",
        description: toHumanErrorMessage(error),
      });
      throw error;
    }
  }

  return { list, get, create, update, remove, runNow };
};
